import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import {
  applyTabIdentity,
  badgeFromLabel,
  DEFAULT_TAB_TITLE,
  restoreDefaultTabIdentity,
} from '../utils/tabIdentity';
import { studentLabel } from '../utils/studentLabel';

/**
 * Короткие имена разделов для вкладок.
 * Более специфичные пути — раньше (первый match побеждает).
 */
export const ROUTE_TAB_DEFAULTS = [
  { path: '/catalog/print/:tagSlug', title: 'Печать', badge: 'П', colorKey: 'print' },
  { path: '/catalog', title: 'Каталог', badge: 'К', colorKey: 'catalog' },
  { path: '/teacher/billing/:studentId', title: 'Счёт', badge: 'С', colorKey: 'billing' },
  { path: '/teacher/billing', title: 'Оплаты', badge: 'О', colorKey: 'billing' },
  { path: '/teacher/students/:studentId/questionnaire', title: 'Анкета', badge: 'А', colorKey: 'teacher' },
  { path: '/teacher/students/:studentId/activity', title: 'Активность', badge: 'Ж', colorKey: 'teacher' },
  { path: '/teacher/students/:studentId/topic-progress', title: 'Темы', badge: 'Т', colorKey: 'teacher' },
  { path: '/teacher/students/:studentId', title: 'Работы', badge: 'Р', colorKey: 'teacher' },
  { path: '/teacher/messages', title: 'Сообщения', badge: 'С', colorKey: 'teacher' },
  { path: '/teacher/schedule', title: 'Расписание', badge: 'Р', colorKey: 'teacher' },
  { path: '/my-messages', title: 'Сообщения', badge: 'С', colorKey: 'profile' },
  { path: '/teacher/lists/create', title: 'Список', badge: 'С', colorKey: 'teacher' },
  { path: '/teacher/lists', title: 'Списки', badge: 'С', colorKey: 'teacher' },
  { path: '/teacher/lessons', title: 'Уроки', badge: 'У', colorKey: 'teacher' },
  { path: '/teacher/tasks/import', title: 'Импорт', badge: 'И', colorKey: 'teacher' },
  { path: '/teacher/tasks', title: 'Редактор', badge: 'Р', colorKey: 'teacher' },
  { path: '/teacher/tags', title: 'Теги', badge: 'Т', colorKey: 'teacher' },
  { path: '/teacher/ege-families', title: 'Семьи', badge: 'С', colorKey: 'teacher' },
  { path: '/teacher/tools', title: 'Инструменты', badge: 'И', colorKey: 'teacher' },
  { path: '/teacher/new-users/:userId/activity', title: 'Активность', badge: 'А', colorKey: 'teacher' },
  { path: '/teacher/new-users', title: 'Новые', badge: 'Н', colorKey: 'teacher' },
  { path: '/teacher', title: 'Ученики', badge: 'У', colorKey: 'teacher' },
  { path: '/collaborative/:sessionId', title: 'Совм.', badge: 'С', colorKey: 'collab' },
  { path: '/collaborative', title: 'Совм.', badge: 'С', colorKey: 'collab' },
  { path: '/sessions/:lessonSessionId/tasks/:taskSlug', title: 'Урок', badge: 'У', colorKey: 'work' },
  { path: '/work/:taskSlug', title: 'Задача', badge: 'З', colorKey: 'work' },
  { path: '/tasks/:slug', title: 'Задача', badge: 'З', colorKey: 'tasks' },
  { path: '/tasks', title: 'Задачи', badge: 'З', colorKey: 'tasks' },
  { path: '/konspekty/new', title: 'Конспект', badge: 'К', colorKey: 'konspekt' },
  { path: '/konspekty/:slug/edit', title: 'Конспект', badge: 'К', colorKey: 'konspekt' },
  { path: '/konspekty/:slug', title: 'Конспект', badge: 'К', colorKey: 'konspekt' },
  { path: '/konspekty', title: 'Конспекты', badge: 'К', colorKey: 'konspekt' },
  { path: '/billing/return', title: 'Оплата', badge: 'О', colorKey: 'billing' },
  { path: '/billing', title: 'Оплата', badge: 'О', colorKey: 'billing' },
  { path: '/trainers/sessions/:sessionId', title: 'Тренажёр', badge: 'Т', colorKey: 'trainers' },
  { path: '/trainers', title: 'Тренажёры', badge: 'Т', colorKey: 'trainers' },
  { path: '/practice-quizzes', title: 'Тесты', badge: 'Т', colorKey: 'tasks' },
  { path: '/my-tasks', title: 'Мои задачи', badge: 'М', colorKey: 'tasks' },
  { path: '/my-attempts', title: 'Попытки', badge: 'П', colorKey: 'tasks' },
  { path: '/my-lessons', title: 'Уроки', badge: 'У', colorKey: 'tasks' },
  { path: '/my-teachers', title: 'Учителя', badge: 'У', colorKey: 'profile' },
  { path: '/topic-progress', title: 'Темы', badge: 'Т', colorKey: 'tasks' },
  { path: '/oge-python', title: 'ОГЭ', badge: 'О', colorKey: 'tasks' },
  { path: '/themes/osnovy_python_oge', title: 'ОГЭ', badge: 'О', colorKey: 'tasks' },
  { path: '/themes/:slug', title: 'Тема', badge: 'Т', colorKey: 'tasks' },
  { path: '/parent/children/:studentId/topic-progress', title: 'Темы', badge: 'Т', colorKey: 'profile' },
  { path: '/parent/children/:studentId/activity', title: 'Активность', badge: 'А', colorKey: 'profile' },
  { path: '/parent/children/:studentId/questionnaire', title: 'Анкета', badge: 'А', colorKey: 'profile' },
  { path: '/parent/children/:studentId', title: 'Ребёнок', badge: 'Р', colorKey: 'profile' },
  { path: '/parent', title: 'Родитель', badge: 'Р', colorKey: 'profile' },
  { path: '/admin/task-cleanup', title: 'Очистка', badge: 'О', colorKey: 'teacher' },
  { path: '/admin/topic-programs', title: 'Программы', badge: 'П', colorKey: 'teacher' },
  { path: '/settings/editor', title: 'Редактор', badge: 'Р', colorKey: 'profile' },
  { path: '/profile', title: 'Профиль', badge: 'П', colorKey: 'profile' },
  { path: '/questionnaire', title: 'Анкета', badge: 'А', colorKey: 'profile' },
  { path: '/dashboard', title: 'Главная', badge: 'Д', colorKey: 'default' },
  { path: '/login', title: 'Вход', badge: 'В', colorKey: 'auth' },
  { path: '/register', title: 'Регистрация', badge: 'Р', colorKey: 'auth' },
  { path: '/invite/:token', title: 'Приглашение', badge: 'П', colorKey: 'auth' },
  { path: '/parent-invite/:token', title: 'Родитель', badge: 'Р', colorKey: 'auth' },
  { path: '/privacy', title: 'Конфиденциальность', badge: 'П', colorKey: 'default' },
  { path: '/consent', title: 'Согласие', badge: 'С', colorKey: 'default' },
  { path: '/oferta', title: 'Оферта', badge: 'О', colorKey: 'default' },
  { path: '/', title: 'Главная', badge: 'Д', colorKey: 'default', end: true },
];

function matchRouteTabDefault(pathname) {
  for (const row of ROUTE_TAB_DEFAULTS) {
    const m = matchPath({ path: row.path, end: row.end !== false }, pathname);
    if (m) {
      return {
        title: row.title,
        badge: row.badge || badgeFromLabel(row.title),
        colorKey: row.colorKey || 'default',
        params: m.params || {},
      };
    }
  }
  return {
    title: DEFAULT_TAB_TITLE,
    badge: 'D',
    colorKey: 'default',
    params: {},
  };
}

const TabIdentityContext = createContext(null);

export function TabIdentityProvider({ children }) {
  const location = useLocation();
  const routeDefault = useMemo(() => matchRouteTabDefault(location.pathname), [location.pathname]);
  const [override, setOverrideState] = useState(null);
  const overrideRef = useRef(null);

  // Смена маршрута сбрасывает override страницы (новая страница выставит свой).
  useEffect(() => {
    overrideRef.current = null;
    setOverrideState(null);
  }, [location.pathname]);

  const setOverride = useCallback((next) => {
    overrideRef.current = next;
    setOverrideState(next);
  }, []);

  const clearOverride = useCallback(() => {
    overrideRef.current = null;
    setOverrideState(null);
  }, []);

  const effective = override || {
    title: routeDefault.title,
    badge: routeDefault.badge,
    colorKey: routeDefault.colorKey,
  };

  useEffect(() => {
    applyTabIdentity(effective);
  }, [override, routeDefault]);

  useEffect(() => () => {
    restoreDefaultTabIdentity();
  }, []);

  const value = useMemo(
    () => ({
      routeDefault,
      setOverride,
      clearOverride,
    }),
    [routeDefault, setOverride, clearOverride],
  );

  return (
    <TabIdentityContext.Provider value={value}>
      {children}
    </TabIdentityContext.Provider>
  );
}

/**
 * Переопределить вкладку, пока страница смонтирована.
 * Передавайте null / пустой title, пока данные ещё грузятся — останется route default.
 *
 * @param {{ title?: string, badge?: string, colorKey?: string } | null} identity
 */
export function useTabIdentity(identity) {
  const ctx = useContext(TabIdentityContext);

  useEffect(() => {
    if (!ctx) {
      // Вне провайдера — применяем напрямую (напр. тесты).
      if (identity?.title) applyTabIdentity(identity);
      return () => {
        if (identity?.title) restoreDefaultTabIdentity();
      };
    }

    const title = (identity?.title || '').trim();
    if (!title) {
      ctx.clearOverride();
      return undefined;
    }

    ctx.setOverride({
      title,
      badge: identity.badge || badgeFromLabel(title),
      colorKey: identity.colorKey || 'default',
    });

    return () => {
      ctx.clearOverride();
    };
  }, [
    ctx,
    identity?.title,
    identity?.badge,
    identity?.colorKey,
  ]);
}

/**
 * Удобный хелпер: «Маша · работы» + цвет по studentId.
 */
export function useStudentTabIdentity(student, sectionTitle) {
  const label = studentLabel(student);
  const id = student?.id;
  const identity = useMemo(() => {
    if (!label) return null;
    const section = (sectionTitle || '').trim();
    return {
      title: section ? `${label} · ${section}` : label,
      badge: badgeFromLabel(label),
      colorKey: id != null ? `student:${id}` : 'teacher',
    };
  }, [label, id, sectionTitle]);

  useTabIdentity(identity);
}
